// © Microsoft Corporation. All rights reserved.
import { __awaiter } from "tslib";
import { AppType, ExperienceTypes, IndexedDBCache, PageType, WebWorkerMessageId, environment, logger, perfMarker, sendMessage, webWorkerCache } from "../wwCommon";
import { LocalNewsFetchFailed, LocalNewsLocationIdFetchFailed } from "../AppErrors";
import { aadTokenQsKey, aadTokenQsVal, getOneServiceHeaders, shouldMakeEnterpriseRequest, shouldUseEnterpriseDomain } from "./OneServiceRequest";
import { experienceConfigMap } from "../wwResolver";
import { get } from "lodash-es";
import { isPdpDataFeatureEnabled } from "./PdpCardData";
import { tryReformatGuid } from "./Common";
/** Min Local News feed items required */
const minRequiredLocalNewsFeedItems = 5;
/** Topics End Point */
const topicsEndPoint = "https://assets.msn.com/service/";
const LocalNewsDatabaseName = "localNewsContent";
const LocalNewsStoreName = "LocalNews";
let latLongLocation;
/** Registers local news related tasks */
export function registerLocalNewsTasks() {
    if (!isFeatureEnabled()) {
        return;
    }
    latLongLocation = `lat-long-locationid-${environment.pcsApptype}`;
    createLocalNewsCache();
    // Wait for weather config
    experienceConfigMap.subscribe(ExperienceTypes.WeatherCard, (weatherConfig) => {
        const isLocalNewsEnabled = get(weatherConfig, "properties.enableLocalNews");
        // Do not fetch local news if local news is disabled in via config
        if (!isLocalNewsEnabled) {
            return;
        }
        if (isPdpDataFeatureEnabled()) {
            webWorkerCache.subscribe(WebWorkerMessageId.WeatherPdp, (data) => {
                const weatherPdpData = data && data.payload;
                fetchLocalNews(weatherPdpData);
            });
        }
        else {
            fetchLocalNews();
        }
    });
}
export let localNewsCache;
/** Makes fetch call for local news and marks cache state as fetching item. */
export function fetchLocalNews(weatherPdpData) {
    return __awaiter(this, void 0, void 0, function* () {
        const locationInfo = getActiveWeatherLocation(weatherPdpData);
        if (!locationInfo) {
            return;
        }
        const ocidLocalNews = getOneServiceOCIDLocal();
        perfMarker("fetchLocalNewsStart");
        const locationData = {
            lat: locationInfo.latitude,
            long: locationInfo.longitude,
            id: latLongLocation,
            storageTimestamp: Date.now()
        };
        try {
            yield localNewsCache.initialize();
            // Always check if there is entry in cache and then avoid making calls.
            const data = localNewsCache.get(latLongLocation);
            if (data && data.lat && data.long && data.lat === locationInfo.latitude && data.long === locationInfo.longitude) {
                return;
            }
            const url = getLocalNewsUrl(locationInfo, ocidLocalNews);
            const init = {
                credentials: "include",
                headers: getOneServiceHeaders()
            };
            const response = yield fetch(url, init);
            if (response && !response.ok) {
                throw Error("fetchLocal News call failed");
            }
            const responseData = yield response.json();
            const locationId = responseData && responseData.id;
            if (locationId) {
                const isLocalNewsPresent = yield fetchLocalNewsForLocationId(locationId, ocidLocalNews);
                localNewsCache.set(latLongLocation, Object.assign(Object.assign({}, locationData), { locationId,
                    isLocalNewsPresent }));
                localNewsCache.saveToIndexedDB();
                return;
            }
            logger.info("WebWorker.fetchLocalNews: Insufficient local news");
            localNewsCache.set(latLongLocation, Object.assign(Object.assign({}, locationData), { locationId: null, isLocalNewsPresent: false }));
            localNewsCache.saveToIndexedDB();
            fetchLocalNewsComplete(false);
        }
        catch (ex) {
            logger.error(Object.assign(Object.assign({}, LocalNewsFetchFailed), { message: "LocalNews failed", pb: Object.assign(Object.assign({}, LocalNewsFetchFailed.pb), { customMessage: `Error: ${ex}. Details: ${JSON.stringify(locationData)}.` }) }));
            localNewsCache.set(latLongLocation, Object.assign(Object.assign({}, locationData), { locationId: null, isLocalNewsPresent: false }));
            localNewsCache.saveToIndexedDB();
            fetchLocalNewsComplete(false);
        }
    });
}
/** Makes fetch call for local news and marks cache state as fetching item.
 * @returns boolean represents whether sufficient local news is present or not
 */
export function fetchLocalNewsForLocationId(locationId, ocidLocalNews) {
    const url = getLocalNewsUrlForLocationId(locationId, ocidLocalNews);
    const init = {
        credentials: "include",
        headers: getOneServiceHeaders()
    };
    return fetch(url, init).then((response) => {
        if (!response.ok) {
            throw Error(`fetchLocal News call failed with status code [${response.status}] for url: ${url}`);
        }
        return response.json();
    }).then(data => {
        if (data && data.feedItems && data.feedItems.length >= minRequiredLocalNewsFeedItems) {
            fetchLocalNewsComplete(true);
            return true;
        }
        else {
            logger.info("WebWorker.fetchLocalNews: Insufficient local news");
            fetchLocalNewsComplete(false);
            return false;
        }
    }).catch(reason => {
        logger.error(Object.assign(Object.assign({}, LocalNewsLocationIdFetchFailed), { message: "LocalNews fetch failed for location", pb: Object.assign(Object.assign({}, LocalNewsLocationIdFetchFailed.pb), { customMessage: `Error: ${reason}. LocationId: ${locationId}. ocidLocalNews: ${ocidLocalNews}` }) }));
        fetchLocalNewsComplete(false);
        return false;
    });
}
/** Marks cache state complete for local news fetch. Responds to any pending message for app anon cookie. */
export function fetchLocalNewsComplete(isLocalNewsPresent) {
    perfMarker("fetchLocalNewsEnd");
    logger.info(`WebWorker.fetchLocalNewsComplete: [id ${WebWorkerMessageId.LocalNews}]`);
    webWorkerCache.set(WebWorkerMessageId.LocalNews, { payload: { isLocalNewsPresent } });
    sendMessage({ id: WebWorkerMessageId.LocalNews, fetched: true, payload: { isLocalNewsPresent } });
}
/** Gets the active weather location */
function getActiveWeatherLocation(weatherPdpData) {
    if (weatherPdpData) {
        const { value = [] } = weatherPdpData;
        const pdpResponse = convertPdpDataToPdpResponse(value);
        if (pdpResponse && pdpResponse.myLocation && pdpResponse.myLocation.latitude && pdpResponse.myLocation.longitude) {
            return pdpResponse.myLocation;
        }
    }
    return environment.detectLocationCookie ? getGeoLocation() : (getHomeLocationfromCookies() || getGeoLocation());
}
/**
 * Returns the pdp get Url
 * @param locationInfo location information
 * @param ocidLocalNews the ocid for local news feed
 * @returns - The url
 */
function getLocalNewsUrl(locationInfo, ocidLocalNews) {
    const isEnterpriseRequest = shouldMakeEnterpriseRequest();
    const isEnterpriseDomain = shouldUseEnterpriseDomain();
    const activityId = environment.activityId && environment.activityId.length ? `&activityId=${tryReformatGuid(environment.activityId)}` : "";
    const fdhead = environment.currentFlights && environment.currentFlights.length ? `&fdhead=${environment.currentFlights}` : "";
    const ocid = `&ocid=${ocidLocalNews}`;
    const locationParam = `&query=location:${locationInfo.latitude}|${locationInfo.longitude}`;
    const wrapData = "&wrapodata=false";
    const disableSerialization = "&DisableTypeSerialization=true";
    const aad = isEnterpriseRequest ? `&${aadTokenQsKey}=${aadTokenQsVal}` : "";
    const urlHref = isEnterpriseDomain ? environment.enterpriseServiceUrlBase : environment.feedServiceUrlBase;
    if (!isEnterpriseDomain) {
        urlHref.href = topicsEndPoint;
    }
    const url = new URL(`news/topics${urlHref.search || "?"}&apiKey=${environment.oneServiceApiKey}${activityId}${ocid}&market=${environment.locale}${fdhead}${locationParam}${wrapData}${disableSerialization}${aad}`, urlHref);
    return url.href;
}
/**
 * Returns the pdp get Url
 * @param location id
 * @returns - The url
 */
function getLocalNewsUrlForLocationId(locationId, ocidLocalNews) {
    const isEnterpriseRequest = shouldMakeEnterpriseRequest();
    const activityId = environment.activityId && environment.activityId.length ? `&activityId=${tryReformatGuid(environment.activityId)}` : "";
    const fdhead = environment.currentFlights && environment.currentFlights.length ? `&fdhead=${environment.currentFlights}` : "";
    const ocid = `&ocid=${ocidLocalNews}`;
    const locationParam = `&ids=${locationId}`;
    const wrapData = "&wrapodata=false";
    const disableSerialization = "&DisableTypeSerialization=true";
    const topParam = "&$top=5";
    const responseSchema = "&responseschema=flatview";
    const aad = isEnterpriseRequest ? `&${aadTokenQsKey}=${aadTokenQsVal}` : "";
    const urlBase = shouldUseEnterpriseDomain() ? environment.enterpriseServiceUrlBase : environment.feedServiceUrlBase;
    const url = new URL(`msn/feed${urlBase.search || "?"}&apiKey=${environment.oneServiceApiKey}${activityId}${ocid}&market=${environment.locale}${fdhead}${locationParam}${responseSchema}${wrapData}${disableSerialization}${topParam}${aad}`, urlBase);
    return url.href;
}
/**
 * Get home location from cookies
 */
function getHomeLocationfromCookies() {
    const homeLocationCookie = environment.homeLocationCookie;
    if (!homeLocationCookie || !homeLocationCookie.length) {
        return null;
    }
    const values = homeLocationCookie.split("|");
    if (values.length > 3 && values[2] && values[3]) {
        return {
            longitude: decodeURIComponent(values[3]),
            latitude: decodeURIComponent(values[2]),
            locality: values[4] && decodeURIComponent(values[4]),
            region: values[5] && decodeURIComponent(values[5]),
            country: values[6] && decodeURIComponent(values[6])
        };
    }
}
/**
 * Get geo Location, this should come from PCS
 */
export function getGeoLocation() {
    const { latitude, longitude } = environment;
    return {
        latitude,
        longitude
    };
}
/**
 * Convert PDP Data to PDP Response
 * @returns {WeatherPdpResponse} WeatherPdpResponse
 *
 */
function convertPdpDataToPdpResponse(response) {
    if (!response || !response.length) {
        return null;
    }
    const defaultLocations = response.filter(value => value.degree === "DefaultLocation");
    const homeLocation = (defaultLocations && defaultLocations.length > 0) ?
        defaultLocations.sort((a, b) => new Date(b.updatedDateTime).valueOf() - new Date(a.updatedDateTime).valueOf())[0] :
        null;
    if (!homeLocation || !homeLocation.definitionName) {
        return null;
    }
    const latLong = homeLocation && homeLocation.definitionName && homeLocation.definitionName.split(",");
    let myLocation = null;
    if (latLong && latLong.length > 1) {
        myLocation = {
            latitude: latLong[0],
            longitude: latLong[1],
            locality: homeLocation.metadata && homeLocation.metadata.city,
            region: homeLocation.metadata && homeLocation.metadata.state,
            country: homeLocation.metadata && homeLocation.metadata.countryRegion,
            displayName: "",
            isoCode: homeLocation.metadata && homeLocation.metadata.isoCode
        };
    }
    const mysetting = (homeLocation.metadata && homeLocation.metadata.displayunit === "2") ? WeatherUnit.Celsius : WeatherUnit.Fahrenheit;
    return {
        myLocation,
        unit: mysetting,
        favLocations: response.filter(value => value.degree === "FavoriteLocation")
    };
}
/**
 * Returns the OCID very specific for local news
 * @returns {string}
 */
export function getOneServiceOCIDLocal() {
    switch (environment.apptype) {
        case AppType.Edge:
            if (environment.pagetype === PageType.DHP) {
                return "spartan-dhp-local";
            }
            if (environment.pagetype === PageType.NTP) {
                return "spartan-ntp-local";
            }
            return "peregrine";
        case AppType.EdgeChromium:
            if (environment.pagetype === PageType.DHP) {
                return "anaheim-dhp-local";
            }
            if (environment.pagetype === PageType.NTP) {
                return "anaheim-ntp-local";
            }
            return "peregrine";
        case AppType.MicrosoftNews:
            return "msftnews-msnews-local";
        default:
            return "peregrine";
    }
}
/**
 * Creates local new indexed db cache instance
 * @param storeName - the name of the indexeddb store
 */
export function createLocalNewsCache() {
    localNewsCache = new IndexedDBCache({
        dbName: LocalNewsDatabaseName,
        storeName: LocalNewsStoreName,
        indexName: `lat-long-locationid-${environment.pcsApptype}`,
        convertToMap: (data) => {
            return new Map().set(data.id, data);
        }
    });
}
/**
 * Check if local news feature is enabled.
 * @returns true if enabled.
 */
function isFeatureEnabled() {
    const { apptype } = environment;
    return apptype === AppType.EdgeChromium || apptype === AppType.SuperApp || apptype === AppType.Windows;
}
/**
 * The Weather Units
 * @export
 * @interface
 */
export var WeatherUnit;
(function (WeatherUnit) {
    WeatherUnit[WeatherUnit["Fahrenheit"] = 1] = "Fahrenheit";
    WeatherUnit[WeatherUnit["Celsius"] = 2] = "Celsius";
})(WeatherUnit || (WeatherUnit = {}));
//# sourceMappingURL=LocalNews.js.map